import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, concatMap, map, switchMap, tap } from 'rxjs';
import { LanguageService } from '@app/i18n';
import { profileActions } from '../actions/profile.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';

@Injectable()
export class ProfileEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly languageService = inject(LanguageService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    getProfile$ = createEffect(() => {
        return this.actions.pipe(
            ofType(profileActions.getProfile),
            switchMap(() => {
                return this.httpService.getProfile().pipe(
                    finalizeLoader('profile-get'),
                    map((profile) => profileActions.getProfileSuccess({ payload: profile })),
                    catchError((response: HttpErrorResponse) => of(profileActions.getProfileError({ code: response.status }))),
                );
            }),
        );
    });

    updateLanguage$ = createEffect(() => {
        return this.actions.pipe(
            ofType(profileActions.updateLanguage),
            concatMap(({ language }) => {
                return this.httpService.updateLanguage(this.languageService.toCode(language)).pipe(
                    map((profile) => profileActions.updateLanguageSuccess({ payload: profile })),
                    catchError((response: HttpErrorResponse) => of(profileActions.updateLanguageError({ code: response.status }))),
                );
            }),
        );
    });

    // profileLoaded() holds the shell until this action lands, so no authenticated screen paints first.
    applyLanguage$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(profileActions.getProfileSuccess),
                tap(({ payload }) => this.languageService.setByCode(payload.language)),
            );
        },
        { dispatch: false },
    );

    updateLanguageError$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(profileActions.updateLanguageError),
                tap(() => this.messageService.add(this.translateService.instant('profile.language.error'), 'danger')),
            );
        },
        { dispatch: false },
    );
}
